import { createContext, ReactNode, useContext, useMemo } from "react";

import { AppLoader } from "~/components/_layout/AppLoader";
import { RBACPermission } from "~/shared/types/RBACPermission";
import { api } from "~/utils/api";
import { logger } from "~/utils/logger";

type AuthorizationContextState = {
  hasAllPermissions: boolean;
  hasPermission: (permissions: RBACPermission[]) => boolean;
  permissions: RBACPermission[];
  role: string;
  roleHomepage: string;
  isSuperAdmin: boolean;
};

interface AuthorizationProviderProps {
  children: ReactNode;
}

const defaultState: AuthorizationContextState = {
  hasAllPermissions: false,
  hasPermission: () => false,
  permissions: [],
  role: "",
  roleHomepage: "",
  isSuperAdmin: false,
};

const AuthorizationContext = createContext<AuthorizationContextState>(defaultState);

export const AuthorizationProvider: React.FC<AuthorizationProviderProps> = ({ children }) => {
  const { data, isPending: isLoading } = api.authorization.getState.useQuery();

  const hasPermission = (permissions: RBACPermission[]) => {
    if (!permissions.length) {
      logger.warn("No permissions provided to evaluate, skipping");
    }

    if (providerState.hasAllPermissions || providerState.isSuperAdmin) {
      return true;
    }

    for (let i = 0; i < permissions.length; i++) {
      const allowedPermission = permissions[i];
      if (!allowedPermission) {
        continue;
      }
      if (providerState.permissions.includes(allowedPermission)) {
        return true;
      }
    }

    return false;
  };

  const providerState = useMemo<Omit<AuthorizationContextState, "hasPermission">>(() => {
    const state = {
      hasAllPermissions: !!(data?.isSuperAdmin || data?.role?.hasAllPermissions),
      permissions: data?.role?.permissions ?? [],
      role: data?.role?.name ?? "",
      roleHomepage: data?.role?.defaultHomepage ?? "",
      isSuperAdmin: data?.isSuperAdmin ?? false,
    };
    return state;
  }, [data]);

  /** @todo this causes an infinite loop if the user has not selected an org. disabling until it can be re-investigated */
  // useEffect(() => {
  //   if (isLoading) {
  //     return;
  //   }
  //   if (!orgIsEnabled && !providerState.isSuperAdmin) {
  //     router.replace(routes.orgSwitcher);
  //   }
  // }, [providerState, isLoading]);

  if (isLoading) {
    return <AppLoader label="Loading your Role and Permissions" isLoading={isLoading} />;
  }

  return <AuthorizationContext.Provider value={{ ...providerState, hasPermission }}>{children}</AuthorizationContext.Provider>;
};

export const useAuthorization = () => {
  const context = useContext(AuthorizationContext);
  if (!context) {
    throw new Error("useAuthorization must be used within a AuthorizationProvider");
  }
  return context;
};
